import { getValueByPath, mergeDeepAll, Singleton } from '@skylab-shared/utils';
import axios from 'axios';
import { ConsoleLogger } from '../../logger';


@Singleton
export class L10nBase {
    //#region lang
    private _lang!: string;
    private logger: ConsoleLogger;
    private hash = new Date().getTime().toString();

    public constructor() {
        this.logger = new ConsoleLogger(this.constructor.name);
    }

    public async setLangAsync(lang: string): Promise<void> {
        if (lang === this._lang) return;

        this.translations = {};
        this._lang = lang;
        const fileRequests = this.readFiles
            .filter(file => file.lang.indexOf(this._lang) === -1)
            .map(file => {
                file.lang.push(this._lang);
                return this.getJsonDataAsync(file.name);
            });
        const res = await Promise.all(fileRequests);
        res.forEach(r => {
            this.mergeTranslations({ [this._lang]: r });
        });
    }

    //#endregion

    //#region filesNames

    private readFiles: { name: string, lang: string[] }[] = [];

    private updateReadFilesInformation(fileName: string): void {
        const file = this.readFiles.find(file => file.name === fileName);
        if (file) {
            if (file.lang.indexOf(this._lang) !== -1) return;
            file.lang.push(this._lang);
        } else {
            this.readFiles.push({
                name: fileName,
                lang: [this._lang]
            });
        }
    }

    //#endregion

    //#region translations
    private translations: Record<string, any> = {};

    public mergeTranslations(translations: Record<string, any>) {
        this.translations = mergeDeepAll([this.translations, translations]);
    }

    public setLanguageFiles(fileNames: string[]): void {
        this.readFiles = [];

        fileNames.forEach(elem => {
            this.updateReadFilesInformation(elem);
        });
    }

    private async getJsonDataAsync(file: string): Promise<any> {
        const url = `/assets/l10n/${file}-${this._lang}.json?v=${this.hash}`;
        const response = await axios.get(url);
        return response.data;
    }

    public _T(path: string, plural?: Record<string, any>): string {
        let value = getValueByPath<string>(this.translations[this._lang], path) || null;
        if (value === null) {
            this.logger.warn(`L10n: lack of translation for key ${path}`);
            return path || '';
        }

        const getInterpolations = (val: string) => val.match(/\{\{.*?\}\}/g);

        if (plural) {
            let interpolations = getInterpolations(value);
            const areInterpolations = () => !!(interpolations && interpolations.length);
            const pluralKeys = Object.keys(plural);
            pluralKeys.sort((a, b) => b.length - a.length);  // ASC  -> a.length - b.length // DESC -> b.length - a.length
            for (let i = 0; i < pluralKeys.length; i++) {
                const key = pluralKeys[i];
                if (!areInterpolations()) break; // stop when no interpolations
                const match = interpolations!.find(interpolation => interpolation.indexOf(key) !== -1);
                if (match) {
                    value = value.replace(match, () => plural[key]);
                    interpolations = getInterpolations(value);
                }
            }
            if (areInterpolations()) {
                this.logger.warn(`L10n: some interpolations were not replaced: ${interpolations}`);
            }
        }
        return value;
    }

    public instant(path: string, plural?: Record<string, any>): string {
        return this._T(path, plural);
    }

    /*
        plural keys are sorted from longest to shortest due to replace conflicts
        plural: { name: 'Nazwa', name2: 'Nazwa2' }
        its made to prevent 'Nazwa' be placed in name2 slot;
    */
    //#endregion
}

export const L10n = new L10nBase();
